import React, { useState, useRef, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { NAV_LINKS } from "../../js/constants";

/**
 * Renders the top-header profile avatar button and its dropdown.
 *
 * Props:
 *   user      – authState.user  (has .contact_info.first_name / last_name / email)
 *   onLogout  – async logout handler (same one passed to SideNav)
 *   permissions – authState.permissions array (for permission-gated links)
 */
export function ProfileDropdown({ user, onLogout, permissions = [] }) {
	const [open, setOpen] = useState(false);
	const wrapRef = useRef(null);

	// Derive display values from user object
	const contactInfo = user?.contact_info ?? {};
	const firstName = contactInfo.first_name ?? "";
	const lastName = contactInfo.last_name ?? "";
	const email = contactInfo.email ?? "";
	const initials =
		(firstName[0] ?? "") + (lastName[0] ?? "") || email[0]?.toUpperCase() || "?";
	const fullName = [firstName, lastName].filter(Boolean).join(" ") || email || "Profile";

	// Close on outside click
	useEffect(() => {
		const handler = (e) => {
			if (wrapRef.current && !wrapRef.current.contains(e.target)) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, []);

	const checkPermissions = (required = []) => {
		if (!required.length) return true;
		return required.some((p) => permissions.includes(p));
	};

	const handleLogout = async (e) => {
		e.preventDefault();
		setOpen(false);
		try {
			await onLogout?.();
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<div className="th-dropdown-wrap" ref={wrapRef}>
			<button
				className="th-avatar-btn"
				onClick={() => setOpen((v) => !v)}
				aria-label="Profile"
				aria-expanded={open}>
				<div className="th-avatar">{initials}</div>
			</button>

			{open && (
				<div className="th-dropdown th-dropdown--profile">
					<div className="th-profile-header">
						<div className="th-profile-avatar">{initials}</div>
						<div>
							<div className="th-profile-name">{fullName}</div>
							{email && <div className="th-profile-email">{email}</div>}
						</div>
					</div>

					<ul className="th-profile-menu">
						{NAV_LINKS.account.map((link) => {
							if (!checkPermissions(link.required)) return null;
							return (
								<li key={link.path}>
									<NavLink
										to={link.path}
										onClick={() => setOpen(false)}>
										{link.name}
									</NavLink>
								</li>
							);
						})}
						<li className="th-profile-divider" />
						<li>
							<a href="/" onClick={handleLogout}>
								Sign Out
							</a>
						</li>
					</ul>
				</div>
			)}
		</div>
	);
}

export default ProfileDropdown;
